import { z } from "zod"

import { matchesSignerAssignment } from "./deal-parties"
import {
  computeFieldRuleState,
  type ComputedFieldRuleState,
  type FormRule,
  type FormRuleContext,
} from "./form-rules"
import {
  getFieldCollectionPhase,
  getFieldChoiceGroup,
  getFieldRecipientAccess,
  getSignTimeRecipientAccess,
  isCheckboxValueChecked,
  type FieldCollectionPhase,
  type FieldRecipientAccess,
  type LegacyFieldCategory,
} from "./template-fields"

const VALIDATION_PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\+?1?\s*\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
  numeric: /^\d+$/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
} as const

export const signingFieldValueSchema = z.union([z.string(), z.boolean()])
export const signingFieldValuesSchema = z.record(z.string(), signingFieldValueSchema)

export type SigningFieldValue = z.infer<typeof signingFieldValueSchema>
export type SigningFieldValues = z.infer<typeof signingFieldValuesSchema>

export type SigningFieldValidationRule = {
  required?: boolean
  minLength?: number
  maxLength?: number
  minChecked?: number
  maxChecked?: number
  pattern?: string
  errorMessage?: string
  email?: boolean
  phone?: boolean
  numeric?: boolean
  alphanumeric?: boolean
  minDate?: string
  maxDate?: string
}

export type SigningRuntimeField = {
  _id: string | number | { toString(): string }
  fieldName: string
  label?: string
  fieldType: string
  validation: SigningFieldValidationRule
  signerRole?: string
  fieldCategory?: LegacyFieldCategory
  recipientAccess?: FieldRecipientAccess
  signTimeRecipientAccess?: FieldRecipientAccess
  collectionPhase?: FieldCollectionPhase
  rules?: FormRule[]
  conditionalOn?: {
    fieldName: string
    operator: "equals" | "not_equals" | "is_filled" | "is_empty" | "checked" | "unchecked"
    value?: string
  }
}

export type ComputedSigningSubmissionState = {
  ruleState: ComputedFieldRuleState
  visibleFieldIds: Set<string>
  requiredFieldIds: Set<string>
  recipientAccessByFieldId: Map<string, FieldRecipientAccess>
}

export type SigningFieldValidationFailure = {
  fieldId: string
  fieldName: string
  label: string
  isRequired: boolean
  errors: string[]
}

export type SigningSubmissionValidationResult = ComputedSigningSubmissionState & {
  failures: SigningFieldValidationFailure[]
}

type CheckboxGroupValidationState = Pick<
  ComputedSigningSubmissionState,
  "visibleFieldIds" | "requiredFieldIds" | "recipientAccessByFieldId"
>

export function normalizeSigningFieldValues(input: unknown): SigningFieldValues {
  return signingFieldValuesSchema.parse(input ?? {})
}

export function computeSigningSubmissionState<TField extends SigningRuntimeField>(
  fields: TField[],
  fieldValues: SigningFieldValues,
  recipientRole: string,
  context: FormRuleContext = {},
  recipientSignerRole?: string,
): ComputedSigningSubmissionState {
  const ruleState = computeFieldRuleState(fields, {
    getFieldId: (field) => String(field._id),
    getFieldValue: (field) => fieldValues[String(field._id)],
    context,
  })

  const visibleFieldIds = new Set<string>()
  const requiredFieldIds = new Set<string>()
  const recipientAccessByFieldId = new Map<string, FieldRecipientAccess>()

  for (const field of fields) {
    const fieldId = String(field._id)
    if (
      !isFieldVisibleToRecipient(field, recipientRole, recipientSignerRole) ||
      ruleState.hiddenFieldIds.has(fieldId)
    ) {
      continue
    }

    visibleFieldIds.add(fieldId)

    const baseRecipientAccess =
      ruleState.recipientAccessOverridesByFieldId.get(fieldId) ?? getFieldRecipientAccess(field)
    const recipientAccess = getSignTimeRecipientAccess({
      ...field,
      recipientAccess: baseRecipientAccess,
      collectionPhase: getFieldCollectionPhase(field),
    })
    recipientAccessByFieldId.set(fieldId, recipientAccess)

    if (isSigningFieldRequired(field, recipientAccess, ruleState)) {
      requiredFieldIds.add(fieldId)
    }
  }

  return {
    ruleState,
    visibleFieldIds,
    requiredFieldIds,
    recipientAccessByFieldId,
  }
}

export function validateSigningRecipientSubmission<TField extends SigningRuntimeField>(
  fields: TField[],
  fieldValues: SigningFieldValues,
  recipientRole: string,
  context: FormRuleContext = {},
  recipientSignerRole?: string,
): SigningSubmissionValidationResult {
  const submissionState = computeSigningSubmissionState(
    fields,
    fieldValues,
    recipientRole,
    context,
    recipientSignerRole,
  )
  const { failures, groupedFieldIds } = collectChoiceGroupValidationFailures(
    fields,
    fieldValues,
    submissionState,
  )

  for (const field of fields) {
    const fieldId = String(field._id)
    if (!submissionState.visibleFieldIds.has(fieldId) || groupedFieldIds.has(fieldId)) {
      continue
    }

    const recipientAccess = submissionState.recipientAccessByFieldId.get(fieldId)
    if (recipientAccess === "readonly") {
      continue
    }

    const isRequired = submissionState.requiredFieldIds.has(fieldId)
    const errors = validateRuntimeFieldValue(fieldValues[fieldId], field, isRequired)
    if (errors.length === 0) {
      continue
    }

    failures.push({
      fieldId,
      fieldName: field.fieldName,
      label: field.label?.trim() || field.fieldName,
      isRequired,
      errors,
    })
  }

  return {
    ...submissionState,
    failures,
  }
}

export function collectChoiceGroupValidationFailures<TField extends SigningRuntimeField>(
  fields: TField[],
  fieldValues: SigningFieldValues,
  submissionState: CheckboxGroupValidationState,
): {
  failures: SigningFieldValidationFailure[]
  groupedFieldIds: Set<string>
} {
  const failures: SigningFieldValidationFailure[] = []
  const groupedFieldIds = new Set<string>()
  const choiceGroups = new Map<
    string,
    {
      fields: TField[]
      label: string
      fieldName: string
      required: boolean
      minChecked?: number
      maxChecked?: number
      selectionMode: "single" | "multi"
    }
  >()

  for (const field of fields) {
    const fieldId = String(field._id)
    if (!submissionState.visibleFieldIds.has(fieldId)) {
      continue
    }

    const recipientAccess = submissionState.recipientAccessByFieldId.get(fieldId)
    if (recipientAccess === "readonly") {
      continue
    }

    const choiceGroup = field.fieldType === "checkbox" ? getFieldChoiceGroup(field) : undefined
    if (!choiceGroup) {
      continue
    }

    const groupKey = `${field.signerRole ?? "*"}::${choiceGroup.groupKey.trim()}`
    const existing = choiceGroups.get(groupKey)
    if (existing) {
      existing.fields.push(field)
      existing.required ||= submissionState.requiredFieldIds.has(fieldId)
      existing.minChecked = pickGreater(existing.minChecked, field.validation.minChecked)
      existing.maxChecked = pickGreater(existing.maxChecked, field.validation.maxChecked)
    } else {
      choiceGroups.set(groupKey, {
        fields: [field],
        label: choiceGroup.groupLabel?.trim() || field.label?.trim() || field.fieldName,
        fieldName: field.fieldName,
        required: submissionState.requiredFieldIds.has(fieldId),
        minChecked: field.validation.minChecked,
        maxChecked: field.validation.maxChecked,
        selectionMode: choiceGroup.selectionMode,
      })
    }
    groupedFieldIds.add(fieldId)
  }

  for (const group of choiceGroups.values()) {
    const checkedCount = group.fields.filter((field) =>
      isCheckboxValueChecked(fieldValues[String(field._id)]),
    ).length
    const errors: string[] = []

    if (group.required && checkedCount === 0) {
      errors.push("This field is required")
    }
    if (group.selectionMode === "single" && checkedCount > 1) {
      errors.push("Select only one option")
    }
    if (group.minChecked !== undefined && checkedCount < group.minChecked) {
      errors.push(`Select at least ${group.minChecked} option${group.minChecked === 1 ? "" : "s"}`)
    }
    if (group.maxChecked !== undefined && checkedCount > group.maxChecked) {
      errors.push(
        `Select no more than ${group.maxChecked} option${group.maxChecked === 1 ? "" : "s"}`,
      )
    }

    if (errors.length === 0) {
      continue
    }

    failures.push({
      fieldId: String(group.fields[0]!._id),
      fieldName: group.fieldName,
      label: group.label,
      isRequired: group.required,
      errors,
    })
  }

  return { failures, groupedFieldIds }
}

export function formatSigningValidationErrorMessage(
  result: Pick<SigningSubmissionValidationResult, "failures">,
) {
  if (result.failures.length === 0) {
    return null
  }

  const requiredFailures = result.failures.filter((failure) =>
    failure.errors.some((error) => error === "This field is required"),
  )

  const labels = [
    ...new Set(
      (requiredFailures.length > 0 ? requiredFailures : result.failures).map(
        (failure) => failure.label,
      ),
    ),
  ]
  const preview = labels.slice(0, 3).join(", ")
  const suffix = labels.length > 3 ? `, and ${labels.length - 3} more` : ""

  if (requiredFailures.length > 0) {
    return `Complete all required fields before submitting: ${preview}${suffix}`
  }

  return `Fix invalid fields before submitting: ${preview}${suffix}`
}

function isFieldVisibleToRecipient(
  field: Pick<SigningRuntimeField, "signerRole">,
  recipientRole: string,
  recipientSignerRole?: string,
) {
  return matchesSignerAssignment(field.signerRole, recipientRole, recipientSignerRole)
}

function isSigningFieldRequired(
  field: SigningRuntimeField,
  recipientAccess: FieldRecipientAccess,
  ruleState: ComputedFieldRuleState,
) {
  if (recipientAccess === "readonly") {
    return false
  }

  const fieldId = String(field._id)
  const requiredOverride = ruleState.requiredOverridesByFieldId.get(fieldId)
  if (requiredOverride !== undefined) {
    return requiredOverride
  }

  if (recipientAccess === "required") {
    return true
  }

  return field.validation.required === true
}

export function validateRuntimeFieldValue(
  value: SigningFieldValue | undefined,
  field: SigningRuntimeField,
  required: boolean,
) {
  const errors: string[] = []

  if (field.fieldType === "checkbox") {
    const checked = isCheckboxValueChecked(value)
    if (required && !checked) {
      return [field.validation.errorMessage || "This field is required"]
    }

    return errors
  }

  if (typeof value !== "string") {
    if (required) {
      return [field.validation.errorMessage || "This field is required"]
    }
    return errors
  }

  const trimmedValue = value.trim()
  if (!trimmedValue) {
    if (required) {
      return [field.validation.errorMessage || "This field is required"]
    }
    return errors
  }

  switch (field.fieldType) {
    case "text":
      if (field.validation.minLength && trimmedValue.length < field.validation.minLength) {
        errors.push(`Minimum length is ${field.validation.minLength} characters`)
      }
      if (field.validation.maxLength && trimmedValue.length > field.validation.maxLength) {
        errors.push(`Maximum length is ${field.validation.maxLength} characters`)
      }
      if (field.validation.email && !VALIDATION_PATTERNS.email.test(trimmedValue)) {
        errors.push(field.validation.errorMessage || "Please enter a valid email address")
      }
      if (field.validation.phone && !VALIDATION_PATTERNS.phone.test(trimmedValue)) {
        errors.push(field.validation.errorMessage || "Please enter a valid phone number")
      }
      if (field.validation.numeric && !VALIDATION_PATTERNS.numeric.test(trimmedValue)) {
        errors.push(field.validation.errorMessage || "Please enter numbers only")
      }
      if (field.validation.alphanumeric && !VALIDATION_PATTERNS.alphanumeric.test(trimmedValue)) {
        errors.push(field.validation.errorMessage || "Please enter letters and numbers only")
      }
      if (field.validation.pattern) {
        try {
          const regex = new RegExp(field.validation.pattern)
          if (!regex.test(trimmedValue)) {
            errors.push(field.validation.errorMessage || "Invalid format")
          }
        } catch {
          errors.push("Invalid field validation pattern")
        }
      }
      break
    case "date": {
      const dateValue = new Date(trimmedValue)
      if (Number.isNaN(dateValue.getTime())) {
        errors.push("Please enter a valid date")
        break
      }

      if (field.validation.minDate) {
        const minDate = new Date(field.validation.minDate)
        if (!Number.isNaN(minDate.getTime()) && dateValue < minDate) {
          errors.push(`Date must be on or after ${minDate.toLocaleDateString("en-US")}`)
        }
      }
      if (field.validation.maxDate) {
        const maxDate = new Date(field.validation.maxDate)
        if (!Number.isNaN(maxDate.getTime()) && dateValue > maxDate) {
          errors.push(`Date must be on or before ${maxDate.toLocaleDateString("en-US")}`)
        }
      }
      break
    }
    case "signature":
    case "initial":
    case "image":
    default:
      break
  }

  return errors
}

function pickGreater(left: number | undefined, right: number | undefined) {
  if (left === undefined) return right
  if (right === undefined) return left
  return Math.max(left, right)
}
